import React, { useState, useEffect, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import ConversationListTable from 'components/tables/ConversationsListTable';
import { Button, Dropdown, Spinner } from 'react-bootstrap';
import Badge, { BadgeBg } from 'components/base/Badge';
import { useAuth } from 'providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import apiClient, { handleAPIError } from 'helpers/apiService';
import AnalysisViewModal from 'pages/meetings/components/AnalysisViewModal';
import { Conversation } from './DataDefinitions';

interface ConversactionsProps {
  contactId: string;
  className?: string;
}

interface ActionDropdownItemsProps {
  resource_id: number;
  status: string;
  handleAction: (row_id: number, command: string) => void;
}


const ActionDropdownItems: React.FC<ActionDropdownItemsProps> = ({ resource_id, status, handleAction }) => {
  return (
    <>
      <Dropdown.Item eventKey="1" onClick={() => handleAction(resource_id, 'analyze')} hidden={status !== 'get feedback'} >Analyze</Dropdown.Item>
      <Dropdown.Item eventKey="2" onClick={() => handleAction(resource_id, "view_analysis")} hidden={status !== 'ready'}>View Feedback</Dropdown.Item>
      <Dropdown.Item eventKey="2" onClick={() => handleAction(resource_id, "export_analysis")} hidden={status !== 'ready'} disabled>Export Feedback</Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item eventKey="4" className="text-danger" onClick={() => handleAction(resource_id, "delete")}>
        Remove
      </Dropdown.Item>
    </>
  );
};

const Conversactions = ({ contactId, className }: ConversactionsProps) => {

  const auth = useAuth();
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysis, setAnalysis] = useState('');
  const [conversations, setConversations] = useState<Conversation[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    // Trigger click event on file input element
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; // Get the first selected file
    // Perform file upload logic here, such as sending the file to your backend
    //console.log('Selected file:', file);
    // Create a FormData object

    if (file) {
      // Create a FormData object
      const formData = new FormData();
      formData.append('file', file);
      formData.append('contact_id', contactId);
      setIsUploading(true);

      const updatedConv = [...conversations];
      const newFile: Conversation = {
        id: -1, name: file.name, contact: '', contact_id: 0, response: '', date: '', status: 'uploading',
        transcription_lang: '', number_of_speakers: ''
      }
      updatedConv.push(newFile);
      setConversations(updatedConv);

      try {
        // Send the file to the backend using Axios
        const response = await apiClient.post<{ filename: string; size: number }>('/v1/resources/upload/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',

          }
        });
        console.log('File uploaded successfully:', response.data);
        // refresh data
        fetchSessionsByContact();
      } catch (error) {
        console.error('Error uploading file:', error);
      } finally {
        setIsUploading(false);
      }
    }

  };

  const analyze = async (row_id: number) => {
    try {
      // Send the file to the backend using Axios
      // ALT+96 -> `
      const response = await apiClient.post(`/v1/api/analyze/?resource_id=${row_id}&lang=EN`, {}, {
        headers: {

        },
        withCredentials: true,
      });
      console.log('Analyze transcription started successfully:', response.data);
      fetchSessionsByContact()

    } catch (error) {
      console.error('Error analyzing file:', error);
    }
  }

  const deleteResource = async (resource_id: number) => {
    try {
      // Send the file to the backend using Axios
      // ALT+96 -> `
      const response = await apiClient.delete(`/v1/resource/${resource_id}`, {
        headers: {

        },
        withCredentials: true,
      });
      if (response.status === 200) {
        console.log('Deleted resource successfully:', response.data);
      }

      fetchSessionsByContact()

    } catch (error) {
      console.error('Error deleteting a resource:', error);
    }
  }

  const handleDropdownItemClick = async (resource_id: number, command: string) => {
    console.log("handleDropdownItemClick " + resource_id + " - " + command);

    if (command === 'analyze')
      analyze(resource_id);

    if (command === 'view_analysis') {
      try {
        const response = await apiClient.get(
          `/v1/analysis/resource/${resource_id}`,
          {
            headers: {
              'accept': 'application/json',

            },
            withCredentials: true,
          }
        );
        setAnalysis(response.data['result']);
        setShowAnalysisModal(true);

      } catch (error) {
        // Handle non-Axios errors
        console.error('Request failed:', error);
      }
    }

    if (command === 'delete') {
      deleteResource(resource_id);
    }
  };

  // Resources/Transcriptions data
  const conversationListTableColumns: ColumnDef<Conversation>[] = [

    {
      accessorKey: 'name',
      header: 'Audio File Name',
      cell: ({ row: { original } }) => {
        const { id, name } = original;
        return (
          <Link to={`/dashboard/conversations/conversation?resource_id=${id}`} className="fw-semi-bold">
            {name}
          </Link>
        );
      },
      meta: {
        cellProps: { className: 'white-space-nowrap py-4' },
        headerProps: { style: { width: '25%' } }
      }
    },

    {
      header: 'Language',
      accessorKey: 'transcription_meta',
      meta: {
        cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4' },
        headerProps: { style: { width: '10%' }, className: 'ps-3' }
      }
    },

    {
      header: 'State',
      accessorKey: 'status',
      meta: {
        //cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4' },
        headerProps: { style: { width: '15%' }, className: 'ps-3' }
      },
      cell: ({ row: { original } }) => {
        const { id, status /*, transcription_meta*/ } = original;
        let bg_color: BadgeBg = 'primary';
        if (status === 'ready') {
          bg_color = 'success';
          return (
            <Badge title='View feedback' variant="phoenix" bg={bg_color} onClick={() => handleDropdownItemClick(id, "view_analysis")} style={{ cursor: 'pointer' }}>
              {status}
            </Badge>
          );
        }

        if (status === 'edit transcript') {
          const title = 'Review Transcript';
          bg_color = 'danger';

          /*
          const onClickHandler = transcription_meta === 'English/American'
            ? () => handleDropdownItemClick(id, "analyze")
            : undefined; // Assign undefined if language doesn't match          
          */

          return (
            <Badge title={title} variant="phoenix" bg={bg_color}
              onClick={() => navigate(`/dashboard/automatic-transcription/conversation?resource_id=${id}`)}
              style={{ cursor: 'pointer' }}>
              {status}
            </Badge>
          );
        }

        return (
          <Link to={`/dashboard/automatic-transcription/conversation?resource_id=${id}`} className="fw-semi-bold">
            <Badge variant="phoenix" bg={bg_color}>
              {status}
            </Badge>
          </Link>
        );
      },
    },
    {
      header: 'Date',
      accessorKey: 'date',
      meta: {
        cellProps: { className: 'ps-3 text-900 py-4' },
        headerProps: { style: { width: '12%' }, className: 'ps-3' }
      }
    },

    /*
    {
      id: 'reflections',
      header: 'Reflections',
      accessorKey: 'reflections',
      meta: {
        cellProps: { className: 'ps-8 py-4' },
        headerProps: { style: { width: '10%' }, className: 'ps-8' }
      }
    },
    */
    {
      id: 'action',
      cell: ({ row: { original } }) => {
        //console.log(original);

        const { id, status } = original;

        return (
          <RevealDropdownTrigger>
            <RevealDropdown>
              <ActionDropdownItems resource_id={id} status={status} handleAction={handleDropdownItemClick} />
            </RevealDropdown>
          </RevealDropdownTrigger>
        )
      },

      /*
      cell: ({ row: { original } }) => {
        const { id } = original;
        return (
          <Link to="#!" className="fw-semi-bold">
            #{id}
          </Link>
        );
      */

      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-end' },
        cellProps: { className: 'text-end' }
      }
    }
  ];

  const fetchSessionsByContact = async () => {
    //console.log("Get contact info: " + contactId);
    try {
      const response = await apiClient.get(
        `/v1/contacts/${contactId}/sessions`,
        {
          headers: {
            'accept': 'application/json',

          }
        }
      );
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching resources by contact data:', error);
    }
  };

  useEffect(() => {
    fetchSessionsByContact();
  }, [contactId]);

  const table = useAdvanceTable({
    data: conversations,
    columns: conversationListTableColumns,
    pageSize: 6,
    pagination: true,
    sortable: true
  });


  return (
    <div>

      <AnalysisViewModal analysis={analysis}
        show={showAnalysisModal}
        handleClose={() => setShowAnalysisModal(false)}
      />

      {/* Hidden file input element */}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />

      <AdvanceTableProvider {...table}>
        <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center">

          <Button className="btn btn-primary px-5" onClick={handleButtonClick}>
            {isUploading && (<>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              /> Uploading in progress...</>)}

            {!isUploading && (<><FontAwesomeIcon icon={faPlus} className="me-2" /> Upload new audio file</>)}
          </Button>
        </div>
        <ConversationListTable />
      </AdvanceTableProvider>
    </div>
  );
};

export default Conversactions;
